import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { dataService } from '@/lib/dataService'
import { useAuth } from '@/contexts/AuthContext'

export const TeacherScoreEntry: React.FC = () => {
  const { user } = useAuth()
  const [assignments, setAssignments] = useState<any[]>([])
  const [sessions, setSessions] = useState<any[]>([])
  const [subjects, setSubjects] = useState<any[]>([])
  const [classrooms, setClassrooms] = useState<any[]>([])
  const [students, setStudents] = useState<any[]>([])
  const [submissions, setSubmissions] = useState<any[]>([])
  const [scores, setScores] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  const loadData = async () => {
    if (!user) return
    setLoading(true)
    try {
      const [allAssignments, allSessions, allSubjects, allClassrooms, allStudents, allSubmissions, allScores] = await Promise.all([
        dataService.getTeachingAssignments(),
        dataService.getAssessmentSessions(),
        dataService.getSubjects(),
        dataService.getClassrooms(),
        dataService.getStudents(),
        dataService.getScoreSubmissions(),
        dataService.getScores()
      ])

      const myAssigns = allAssignments.filter((a: any) => a.teacher_id === user.id)
      setAssignments(myAssigns)
      setSessions(allSessions)
      setSubjects(allSubjects)
      setClassrooms(allClassrooms)
      setStudents(allStudents)
      setSubmissions(allSubmissions)
      setScores(allScores)
    } catch (err) {
      console.error('Error loading score entry dashboard:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [user])

  const getAssignmentProgress = (classroomId: number, subjectId: number) => {
    const studentsInClass = students.filter(s => s.classroom_id === classroomId)
    if (studentsInClass.length === 0) {
      return { total: 0, preFilled: 0, preLeft: 0, postFilled: 0, postLeft: 0, prePercent: 0, postPercent: 0 }
    }
    let preCount = 0
    let postCount = 0
    studentsInClass.forEach(st => {
      const match = scores.find(sc => sc.student_id === st.id && sc.subject_id === subjectId)
      if (match) {
        if (match.pre_test_score !== null && match.pre_test_score !== undefined) preCount++
        if (match.post_test_score !== null && match.post_test_score !== undefined) postCount++
      }
    })
    return {
      total: studentsInClass.length,
      preFilled: preCount,
      preLeft: Math.max(0, studentsInClass.length - preCount),
      postFilled: postCount,
      postLeft: Math.max(0, studentsInClass.length - postCount),
      prePercent: Math.round((preCount / studentsInClass.length) * 100),
      postPercent: Math.round((postCount / studentsInClass.length) * 100)
    }
  }

  const getSubjectName = (id: number) => {
    const s = subjects.find(sub => sub.id === id)
    return s ? `${s.code} ${s.name}` : '—'
  }

  const getClassroomStr = (id: number) => {
    const c = classrooms.find(cl => cl.id === id)
    return c ? `ชั้น ม.${c.grade_level >= 7 ? c.grade_level - 6 : c.grade_level}/${c.room_number}` : '—'
  }

  const getActiveStudentsCount = (classroomId: number) => {
    return students.filter(st => st.classroom_id === classroomId && (!st.status || st.status === 'active')).length
  }

  const getSubmissionStatus = (assignmentId: number) => {
    const currentSession = sessions.find((s: any) => s.is_open) || sessions[0]
    if (!currentSession) return { pre: 'draft', post: 'draft' }
    const sub = submissions.find(s => s.teaching_assignment_id === assignmentId && s.assessment_session_id === currentSession.id)
    if (!sub) return { pre: 'draft', post: 'draft' }
    return { pre: sub.pre_test_status, post: sub.post_test_status }
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' }}>
        <div style={{
          width: '40px',
          height: '40px',
          border: '3px solid rgba(255, 255, 255, 0.1)',
          borderTopColor: 'var(--primary)',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <style>{`
          @keyframes spin { to { transform: rotate(360deg); } }
        `}</style>
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
      {/* Header */}
      <div>
        <h1 style={{ fontSize: '28px', fontWeight: '700', marginBottom: '8px' }}>✏️ กรอกคะแนนสอบ (Pre-Test & Post-Test)</h1>
        <p style={{ color: 'var(--text-secondary)' }}>เลือกห้องเรียนการสอนด้านล่างเพื่อทำการป้อนคะแนน บันทึกแบบร่าง หรือล็อกส่งคะแนนปลายทาง</p>
      </div>

      {/* Active Session Banner */}
      {(() => {
        const activeSession = sessions.find((s: any) => s.is_open)
        const expired = !activeSession || new Date() > new Date(activeSession.end_date)
        const getSessionType = (s: any) => {
          if (!s) return 'none'
          const name = (s.name || '').toLowerCase()
          if (name.includes('ก่อนเรียน') || name.includes('pre')) return 'pre'
          if (name.includes('หลังเรียน') || name.includes('post')) return 'post'
          return 'none'
        }

        if (expired) {
          return (
            <div style={{
              background: 'rgba(239, 68, 68, 0.08)',
              border: '1px solid rgba(239, 68, 68, 0.2)',
              borderRadius: '16px',
              padding: '16px 20px',
              color: 'var(--danger)',
              fontSize: '14px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <span style={{ fontSize: '20px' }}>🔴</span>
              <div>
                <strong style={{ display: 'block', fontSize: '15px', marginBottom: '2px' }}>ปิดระบบบันทึกคะแนน</strong>
                <span>ขณะนี้ยังไม่มีรอบการบันทึกคะแนนที่เปิดใช้งาน หรือพ้นกำหนดระยะเวลากรอกแล้ว กรุณาติดต่อผู้ดูแลระบบ</span>
              </div>
            </div>
          )
        }

        const type = getSessionType(activeSession)
        if (type === 'pre') {
          return (
            <div style={{
              background: 'rgba(16, 185, 129, 0.08)',
              border: '1px solid rgba(16, 185, 129, 0.2)',
              borderRadius: '16px',
              padding: '16px 20px',
              color: 'var(--success)',
              fontSize: '14px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <span style={{ fontSize: '20px' }}>🟢</span>
              <div>
                <strong style={{ display: 'block', fontSize: '15px', marginBottom: '2px' }}>เปิดรอบบันทึกคะแนนก่อนเรียน (Pre-Test)</strong>
                <span>เปิดถึงวันที่ {new Date(activeSession.end_date).toLocaleDateString('th-TH', { day: 'numeric', month: 'long', year: 'numeric' })} - สามารถบันทึกและแก้ไขได้เฉพาะคะแนนก่อนเรียนเท่านั้น</span>
              </div>
            </div>
          )
        }

        if (type === 'post') {
          return (
            <div style={{
              background: 'rgba(245, 158, 11, 0.08)',
              border: '1px solid rgba(245, 158, 11, 0.2)',
              borderRadius: '16px',
              padding: '16px 20px',
              color: '#d97706',
              fontSize: '14px',
              fontWeight: '600',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <span style={{ fontSize: '20px' }}>🟠</span>
              <div>
                <strong style={{ display: 'block', fontSize: '15px', marginBottom: '2px' }}>เปิดรอบบันทึกคะแนนหลังเรียน (Post-Test)</strong>
                <span>เปิดถึงวันที่ {new Date(activeSession.end_date).toLocaleDateString('th-TH', { day: 'numeric', month: 'long', year: 'numeric' })} - สามารถบันทึกและแก้ไขได้เฉพาะคะแนนหลังเรียนเท่านั้น</span>
              </div>
            </div>
          )
        }

        return null
      })()}

      {assignments.length === 0 ? (
        <div className="glassmorphism" style={{ padding: '48px', textAlign: 'center', color: 'var(--text-secondary)' }}>
          📭 ไม่พบห้องเรียนสอนในตารางงานของคุณขณะนี้ กรุณาไปที่เมนู <strong>"จัดการตารางสอน"</strong> เพื่อลงทะเบียนวิชาที่ทำการสอนก่อน
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: '24px' }}>
          {assignments.map((assign) => {
            const status = getSubmissionStatus(assign.id)
            const isPreSubmitted = status.pre === 'submitted'
            const isPostSubmitted = status.post === 'submitted'

            return (
              <div key={assign.id} className="glassmorphism" style={{ padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px', borderLeft: '5px solid var(--primary)' }}>
                <div>
                  <span style={{ fontSize: '11px', color: 'var(--primary)', fontWeight: '700', textTransform: 'uppercase', display: 'block', marginBottom: '4px' }}>
                    ตารางสอนลงทะเบียน
                  </span>
                  <h3 style={{ fontSize: '18px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '4px' }}>
                    {getSubjectName(assign.subject_id)}
                  </h3>
                  <span style={{ fontSize: '14px', color: 'var(--text-secondary)', fontWeight: '600' }}>
                    🏫 {getClassroomStr(assign.classroom_id)}
                  </span>
                </div>

                <div style={{ display: 'flex', gap: '16px', background: 'rgba(0,0,0,0.02)', padding: '12px', borderRadius: '8px', fontSize: '12px' }}>
                  <div style={{ flex: 1 }}>
                    <span style={{ color: 'var(--text-muted)', display: 'block' }}>จำนวนนักเรียน:</span>
                    <strong>{getActiveStudentsCount(assign.classroom_id)} คน (ปกติ)</strong>
                  </div>
                  <div style={{ flex: 1 }}>
                    <span style={{ color: 'var(--text-muted)', display: 'block' }}>สถานะ Pre / Post:</span>
                    <strong>
                      <span style={{ color: isPreSubmitted ? 'var(--success)' : 'var(--warning)' }}>{isPreSubmitted ? 'ส่งแล้ว' : 'แบบร่าง'}</span>
                      {' / '}
                      <span style={{ color: isPostSubmitted ? 'var(--success)' : 'var(--warning)' }}>{isPostSubmitted ? 'ส่งแล้ว' : 'แบบร่าง'}</span>
                    </strong>
                  </div>
                </div>

                {/* PROGRESS BARS */}
                {(() => {
                  const stats = getAssignmentProgress(assign.classroom_id, assign.subject_id)
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', borderTop: '1px solid rgba(0,0,0,0.06)', paddingTop: '10px' }}>
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '2px', color: 'var(--text-secondary)' }}>
                          <span>📝 Pre-Test: <strong>{stats.preFilled}/{stats.total}</strong> คน</span>
                          <span style={{ fontWeight: '600' }}>เหลือ {stats.preLeft} คน</span>
                        </div>
                        <div style={{ width: '100%', height: '5px', background: 'rgba(0,0,0,0.05)', borderRadius: '2.5px', overflow: 'hidden' }}>
                          <div style={{ width: `${stats.prePercent}%`, height: '100%', background: 'linear-gradient(90deg, #3b82f6, #60a5fa)', borderRadius: '2.5px' }} />
                        </div>
                      </div>
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', marginBottom: '2px', color: 'var(--text-secondary)' }}>
                          <span>🎓 Post-Test: <strong>{stats.postFilled}/{stats.total}</strong> คน</span>
                          <span style={{ fontWeight: '600' }}>เหลือ {stats.postLeft} คน</span>
                        </div>
                        <div style={{ width: '100%', height: '5px', background: 'rgba(0,0,0,0.05)', borderRadius: '2.5px', overflow: 'hidden' }}>
                          <div style={{ width: `${stats.postPercent}%`, height: '100%', background: 'linear-gradient(90deg, #10b981, #34d399)', borderRadius: '2.5px' }} />
                        </div>
                      </div>
                    </div>
                  )
                })()}


                <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                  <Link 
                    to={`/teacher/score/${assign.id}`}
                    className="btn btn-primary"
                    style={{ width: '100%', padding: '10px', fontSize: '13px', textAlign: 'center', textDecoration: 'none', fontWeight: '600' }}
                  >
                    ✍️ ดำเนินการป้อนคะแนน
                  </Link>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
export default TeacherScoreEntry
